import { css } from 'styled-components'

/**
 * navStyles — scoped styling for the platform navigation (side nav, flyouts,
 * settings panel). The nav owns its look: these helpers also neutralize the
 * global anchor rules in root.styles.js (`a, a:focus { color: var(--links-color) }`,
 * `a:hover, a:focus:hover { underline }`) which otherwise outrank a bare
 * styled-class rule and repaint a just-clicked nav link light-blue/underlined.
 * Consumers compose these instead of fighting the global stylesheet each.
 */

// Gap (px) between a collapsed rail icon and its hover flyout, passed as the V3
// Tooltip `sideOffset`. The diamond connector is pinned 14px left of the popup
// edge, so its position is `iconRight + FLYOUT_SIDE_OFFSET − 14`. Shared by ALL
// collapsed flyouts (main nav, Settings, locked upsell) so the diamond sits at
// the exact same distance from the rail in every case.
export const FLYOUT_SIDE_OFFSET = 16

// Hover = pale-blue pill only; the label keeps its color/weight and stays
// underline-free (Confluence nav spec "Hover" annotation). Also pins the
// focused (just-clicked) state and replaces the default focus ring with the
// keyboard-only :focus-visible outline.
export const navRowHover = css`
  &:hover,
  &:focus,
  &:focus:hover {
    color: var(--sidebar-item-text);
    text-decoration: none;
  }

  &:hover,
  &:focus:hover {
    background-color: var(--sidebar-item-hover-background);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--color-border);
    outline-offset: -2px;
  }
`

// Current page (Confluence nav spec): bold navy label + 3px blue left bar,
// NO background pill — the pill is exclusively the hover affordance.
// `barLeft`/`barRadius` cover the small placement deltas between surfaces
// (in-rail rows vs flyout rows).
export const navRowActive = ({ barLeft = '4px', barRadius = '4px' } = {}) => css`
  background-color: transparent;
  color: var(--sidebar-item-text);
  font-weight: 700;

  &::before {
    content: '';
    position: absolute;
    left: ${barLeft};
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 20px;
    border-radius: ${barRadius};
    background: var(--primary);
  }

  &:hover,
  &:focus,
  &:focus:hover {
    background-color: transparent;
    color: var(--sidebar-item-text);
    text-decoration: none;
  }
`

// Active SECTION rows (L1, incl. the pinned Settings trigger): design
// correction (2026-07-27, designer review) — the resting active L1 row carries
// NO background fill. Active is signaled by the icon recoloring (NavSection.js,
// out of scope here), not a pale-blue pill; the pill is exclusively the
// transient hover wash from navRowHover, which still applies normally (hover
// and active are not mutually exclusive — hovering the active row still shows
// the hover pill).
export const navSectionActive = css`
  background-color: transparent;
  color: var(--sidebar-item-text);
`
